import { memo, useCallback, useEffect, useState } from 'react';

import { FormikProvider, useFormik } from 'formik';
import useFocusOnFieldError from 'hooks/useFocusOnFieldError';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { orderBumpService } from 'services';
import { analyticsG4, enFeatures } from 'services/analyticsG4';
import { PageColumn, ButtonOutline, If } from 'shared';
import { EmptyState } from 'shared/EmptyState';
import { Button, Column, Row } from 'shared/Houston';
import { orderBumpFormActions } from 'store/features';
import { selectOrderBumpForm } from 'store/selectors/orderBumpForm/index';
import { IOrderBumpForm, IOrderBumpFormItem } from 'types/interfaces';
import { renderToastMessageApi } from 'utils/toastMessage';
import * as Yup from 'yup';

import SaveOutline from '@eduzz/houston-icons/SaveOutline';

import BumpsProductsSelection from './BumpsProductsSelection';
import MainProductSelection from './MainProductSelection';
import OfferTitle from './OfferTitle';
import offerTitleOptions from './OfferTitle/options';

interface IProps {
  handleCancelForm?: () => void;
}

const OrderBumpForm = ({ handleCancelForm }: IProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const orderBumpForm = useSelector(selectOrderBumpForm);
  const { mainProduct, orderBumpsProducts, isLoading } = orderBumpForm;
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const redirectToOrderBumpList = useCallback(() => {
    navigate('/orderbump', { replace: true });
  }, [navigate]);

  const form = useFormik<IOrderBumpForm<IOrderBumpFormItem>>({
    initialValues: {
      contentId: undefined,
      title: offerTitleOptions[0].label,
      items: []
    },
    validationSchema: Yup.object().shape({
      title: Yup.string()
        .test('offerTitle', 'Título da oferta obrigatório', val =>
          offerTitleOptions.some(options => options.label === val)
        )
        .required('Título da oferta obrigatório')
        .nullable()
    }),
    onSubmit: async data => {
      try {
        if (isEditing) {
          await orderBumpService.editOrderBump(data);
        } else {
          await orderBumpService.saveOrderBump(data);
        }

        analyticsG4.sendSuccess(enFeatures.ORDER_BUMP);

        renderToastMessageApi(200, 'Order Bump salvo com sucesso');
        redirectToOrderBumpList();
      } catch {}
    }
  });

  useFocusOnFieldError(form);

  const loadOrderBump = useCallback(
    async (idParam: string) => {
      try {
        if (isEditing || isLoading) {
          return;
        }

        dispatch(orderBumpFormActions.setIsLoading(true));
        setIsEditing(true);

        const { id, title, description, contentId, items, status, mainProduct } = await orderBumpService.getOrderBump(
          Number(idParam)
        );

        form.setFieldValue('id', id);
        form.setFieldValue('title', title);
        form.setFieldValue('description', description);
        form.setFieldValue('contentId', contentId);
        form.setFieldValue('offerId', mainProduct?.offerId);
        form.setFieldValue('items', items);
        form.setFieldValue('status', status);

        dispatch(orderBumpFormActions.setIsLoading(false));
      } catch (_) {
        setShowEmptyState(true);
        dispatch(orderBumpFormActions.setIsLoading(false));
      }
    },
    [isEditing, isLoading, form, dispatch]
  );

  useEffect(() => {
    if (!id) {
      return;
    }

    loadOrderBump(id);
  }, [id, loadOrderBump]);

  const handleSaveOrderBump = useCallback(() => {
    const { contentId } = form.values;

    if (contentId) {
      dispatch(orderBumpFormActions.addContentIdForClearCache(contentId));
    }

    form.submitForm();
  }, [form, dispatch]);

  useEffect(() => {
    analyticsG4.sendPageView({ title: 'Checkout Sun - Configurar Order Bump' });
  }, []);

  if (showEmptyState && !isLoading) {
    return (
      <EmptyState
        width='65%'
        title='Order Bump não encontrado &#128532;'
        subtitle={
          <span>
            Você pode cadastrar um novo Order Bump <a href='/orderbump/create'>clicando aqui!</a>
          </span>
        }
      />
    );
  }

  return (
    <FormikProvider value={form}>
      <PageColumn>
        <OfferTitle />
      </PageColumn>
      <PageColumn>
        <MainProductSelection />
      </PageColumn>
      <PageColumn>
        <If condition={!!mainProduct || isLoading}>
          <BumpsProductsSelection />
        </If>
      </PageColumn>
      <Row justifyContent='end'>
        <Column>
          <ButtonOutline onClick={handleCancelForm}>Cancelar</ButtonOutline>
        </Column>
        <Column>
          <Button
            onClick={handleSaveOrderBump}
            startIcon={<SaveOutline />}
            disabled={!mainProduct || orderBumpsProducts?.length === 0}
            loading={form.isSubmitting}
          >
            Salvar
          </Button>
        </Column>
      </Row>
    </FormikProvider>
  );
};

export default memo(OrderBumpForm);
